import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import { Button, ConfirmationModal } from '@components/common'
import { EmptyState } from '@components/ui'
import { getFaxFileSignedUrl } from '@services/api/fax/document.api'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  applyShowImagesToHtml,
  getFaxCoversheetMeta,
  getFaxPreviewContent,
  parseCoversheetContent,
  parseDraftComposeState,
} from '@utils/fax-coversheet.util'
import { sanitizeEmailHtml } from '@utils/sanitize-html.util'
import { formatFileSize, getFaxCounterpartNumber } from '@utils/fax.util'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import {
  IFaxCoversheetContent,
  IFaxFolder,
  IFaxMessage,
  IFaxTag,
} from '@dataTypes/fax.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  setActiveFax,
  setActiveFolder,
  setFaxComposeSeed,
} from '@store/reducers/fax.reducer'
import type { FaxPreviewHooks } from './useFaxPreview'
import FaxDetailToolbar from './FaxDetailToolbar.component'
import moment from 'moment'

import { logger } from '@utils/safeLogger'

type Props = {
  fax: IFaxMessage
  hooks: FaxPreviewHooks
  folders: IFaxFolder[]
  tags: IFaxTag[]
}

const FAX_PREVIEW_LOADING_CONTAINER = '.fax-preview-container'

const waitForImages = async (container: HTMLElement): Promise<void> => {
  const imgs = Array.from(
    container.querySelectorAll('img'),
  ) as HTMLImageElement[]
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) return resolve()
          img.addEventListener('load', () => resolve(), { once: true })
          img.addEventListener('error', () => resolve(), { once: true })
        }),
    ),
  )
}

const FaxDetailPreview: React.FC<Props> = ({ fax, hooks, folders, tags }) => {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const showImages = useAppSelector((state) => state.fax.settings?.show_images)
  const [imagesRevealed, setImagesRevealed] = useState(false)
  const [showImageConfirm, setShowImageConfirm] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  const printRef = useRef<HTMLDivElement>(null)
  const preview = getFaxPreviewContent(fax)
  const hasContents = preview.kind === 'html' && Boolean(preview.html?.trim())
  const rawHtml = hasContents ? (preview.html ?? '') : ''
  const html = hasContents
    ? sanitizeEmailHtml(
        applyShowImagesToHtml(rawHtml, showImages, imagesRevealed),
      )
    : ''
  const hasAttachments = (fax.documents?.length ?? 0) > 0
  const isInbox = hooks.isInboxView

  useEffect(() => {
    setImagesRevealed(false)
    setShowImageConfirm(false)
  }, [fax.fax_message_id])

  useEffect(() => {
    const node = bodyRef.current
    if (!node || showImages !== 'ask' || imagesRevealed) return undefined

    const isPlaceholder = (target: EventTarget | null): boolean =>
      target instanceof Element &&
      Boolean(target.closest('[data-fax-image-placeholder]'))

    const handleClick = (event: MouseEvent): void => {
      if (isPlaceholder(event.target)) setShowImageConfirm(true)
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      if (isPlaceholder(event.target)) {
        event.preventDefault()
        setShowImageConfirm(true)
      }
    }

    node.addEventListener('click', handleClick)
    node.addEventListener('keydown', handleKeyDown)
    return () => {
      node.removeEventListener('click', handleClick)
      node.removeEventListener('keydown', handleKeyDown)
    }
  }, [showImages, imagesRevealed, html])

  const confirmShowImages = useCallback(() => {
    setImagesRevealed(true)
    setShowImageConfirm(false)
  }, [])
  const meta = getFaxCoversheetMeta(fax)

  const toLabel =
    meta.toLabel || (isInbox ? 'Clinic' : hooks.getDisplayLabel(fax))
  const fromLabel =
    meta.fromLabel || (isInbox ? hooks.getDisplayLabel(fax) : hooks.clinicName)
  const counterpartNumber = getFaxCounterpartNumber(fax, isInbox)
  const formattedCounterpart = counterpartNumber
    ? formatPhoneNumber(counterpartNumber)
    : ''
  const dateDisplay =
    meta.sentOn ||
    (fax.date_time_created_at
      ? moment(fax.date_time_created_at).format('MMM D, YYYY h:mm A')
      : '-')
  const title = meta.subject || fax.subject || 'Fax'

  const handleDownload = useCallback(
    async (fileUrl: string, fileName: string) => {
      showLoading({ container: FAX_PREVIEW_LOADING_CONTAINER })
      try {
        const signed = await getFaxFileSignedUrl(fileUrl)
        const signedUrl = signed.data?.url
        if (signed.statusCode >= 400 || !signedUrl) {
          throw new Error('Failed to get a signed download URL.')
        }

        const response = await fetch(signedUrl)
        if (!response.ok) {
          throw new Error(`Download request failed (${response.status}).`)
        }
        const blob = await response.blob()

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        showToast(
          createSuccessToast(
            'Download completed',
            `"${fileName}" downloaded successfully.`,
          ),
        )
      } catch (e) {
        console.error(e)
        showToast(
          createFailedToast(
            'Download failed',
            `Could not download "${fileName}". Please try again.`,
          ),
        )
      } finally {
        hideLoading(FAX_PREVIEW_LOADING_CONTAINER)
      }
    },
    [showLoading, hideLoading, showToast],
  )

  const resolveCoverContent = (): IFaxCoversheetContent | undefined => {
    const fromDraft = parseDraftComposeState(
      fax.draft_compose_state,
    )?.coverContent
    if (fromDraft) return fromDraft
    if (fax.coversheet?.content) {
      return parseCoversheetContent(fax.coversheet.content) ?? undefined
    }
    return undefined
  }

  const continueEditing = (): void => {
    dispatch(setActiveFax(fax))
    dispatch(setActiveFolder('compose'))
  }

  const handleReply = (): void => {
    dispatch(setActiveFax(null))
    dispatch(
      setFaxComposeSeed({
        intent: 'reply',
        subject: `Re: ${fax.subject ?? ''}`.trim(),
        contactId: fax.contact_id ?? null,
        coversheetMode: 'skipped',
      }),
    )
    dispatch(setActiveFolder('compose'))
  }

  const handleForward = (): void => {
    const coverContent = resolveCoverContent()
    dispatch(setActiveFax(null))
    dispatch(
      setFaxComposeSeed({
        intent: 'forward',
        subject: `Fwd: ${fax.subject ?? ''}`.trim(),
        contactId: null,
        coversheetMode: coverContent ? 'template' : 'skipped',
        coverContent,
      }),
    )
    dispatch(setActiveFolder('compose'))
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: fax.subject?.trim() || 'Fax',
    pageStyle: `
      @page { margin: 0.75in 1in; }
      body { margin: 0; color: #000; font-family: Inter, Arial, sans-serif; }
    `,
    onBeforeGetContent: async () => {
      if (printRef.current) {
        await waitForImages(printRef.current)
      }
    },
  })

  const handlePrintClick = useCallback(async (): Promise<void> => {
    try {
      await handlePrint()
    } catch (error) {
      logger.error('Print failed:', error)
      showToast(
        createFailedToast(
          'Print failed',
          'Could not print this fax. Try again.',
        ),
      )
    }
  }, [handlePrint, showToast])

  const isDraft = fax.status === 'draft'
  const isTrash = hooks.activeFolder === 'trash'
  const canReplyForward = !isDraft && !isTrash
  const canRetry = fax.status === 'failed' && fax.direction === 'Outgoing'

  return (
    <>
      <div className="fax-detail-preview">
        <button
          type="button"
          className="fax-detail-back"
          onClick={hooks.handleBackFromDetail}
        >
          <i className="mgc_left_line" />
          Back
        </button>

        <div className="fax-detail-meta-card">
          <h2 className="fax-detail-title">{title}</h2>
          <div className="fax-detail-subtitle">
            <span className="fax-detail-subtitle-name">{fromLabel || '-'}</span>
            {formattedCounterpart ? (
              <span className="fax-detail-subtitle-phone">
                {formattedCounterpart}
              </span>
            ) : null}
          </div>

          <div className="fax-detail-meta-grid">
            <div className="fax-detail-meta-row">
              <span className="fax-detail-meta-label">To:</span>
              <span>{toLabel || '-'}</span>
            </div>
            <div className="fax-detail-meta-row">
              <span className="fax-detail-meta-label">Date:</span>
              <span>{dateDisplay}</span>
            </div>
            {meta.customFields.map((field) => (
              <div
                key={`${field.label}-${field.value}`}
                className="fax-detail-meta-row"
              >
                <span className="fax-detail-meta-label">
                  {field.label ? `${field.label}:` : 'Field:'}
                </span>
                <span>{field.value || '-'}</span>
              </div>
            ))}
            <div className="fax-detail-meta-row">
              <span className="fax-detail-meta-label">Pages:</span>
              <span>{meta.pageCount}</span>
            </div>
          </div>

          {(isDraft || canRetry) && (
            <div className="fax-detail-primary-actions">
              {isDraft && (
                <Button
                  className="button-primary"
                  size="sm"
                  onClick={continueEditing}
                >
                  Continue editing
                </Button>
              )}
              {canRetry && (
                <Button
                  className="button-primary"
                  size="sm"
                  onClick={() => hooks.onRetry(fax)}
                  leftIcon={<i className="mgc_refresh_2_line" />}
                >
                  Retry
                </Button>
              )}
            </div>
          )}

          <FaxDetailToolbar
            fax={fax}
            hooks={hooks}
            folders={folders}
            tags={tags}
            canReplyForward={canReplyForward}
            onReply={handleReply}
            onForward={handleForward}
            onPrint={() => void handlePrintClick()}
          />
        </div>

        <div ref={printRef} className="fax-detail-printable">
          <div className="fax-detail-body-container">
            <div ref={bodyRef} className="fax-detail-body">
              {hasContents ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: html,
                  }}
                />
              ) : (
                <EmptyState
                  className="fax-contents-empty"
                  iconClass="mgc mgc_fax_fill"
                  title="No contents to show"
                  description={
                    preview.kind === 'not_available'
                      ? 'Preview is not available for this coversheet.'
                      : 'This fax has no coversheet content.'
                  }
                />
              )}
            </div>
            <div className="fax-detail-attachments">
              <span className="fax-detail-attachments-title">Attachments</span>
              {hasAttachments ? (
                <div className="fax-attachment-cards">
                  {fax.documents?.map((doc) => (
                    <div
                      key={doc.fax_document_id}
                      className="fax-attachment-card"
                    >
                      <div>
                        <div
                          className="fax-attachment-title"
                          title={doc.file_name || 'Document'}
                          data-bs-toggle="tooltip"
                        >
                          {doc.file_name || 'Document'}
                        </div>
                        <div className="fax-attachment-meta">
                          {doc.mime_type?.includes('pdf') ? 'PDF file' : 'File'}
                          {doc.file_size
                            ? ` - ${formatFileSize(doc.file_size)}`
                            : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="unstyled-button fax-attachment-download mgc_download_2_fill fax-attachment-download-screen"
                        aria-label={`Download ${doc.file_name || 'document'}`}
                        onClick={() =>
                          handleDownload(
                            doc.file_url,
                            doc.file_name || 'document.pdf',
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  className="fax-attachments-empty"
                  iconClass="mgc mgc_attachment_fill"
                  title="No attachments"
                  description="There are no files attached to this fax."
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        show={showImageConfirm}
        onHide={() => setShowImageConfirm(false)}
        title="Show images"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          <div className="confirmation-modal__body">
            <p className="confirmation-modal__title">Show images?</p>
            <p className="confirmation-modal__message">
              Images in this fax are hidden. Do you want to display all images
              in this coversheet?
            </p>
          </div>
        }
        footer={
          <div className="confirmation-modal__footer">
            <Button
              className="button-secondary"
              size="md"
              onClick={() => setShowImageConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              className="button-primary"
              size="md"
              onClick={confirmShowImages}
            >
              Confirm
            </Button>
          </div>
        }
      />
    </>
  )
}

export default FaxDetailPreview
